// Direct file import, not the '@/utils/models' barrel: this module is imported almost everywhere
// (client islands too), and the barrel drags in the generated GraphQL module (+ React Query) and
// the navigation SVGs.
import { Alphabet } from './models/inventory-items';
import { GeneralPath } from './models/page-paths';

export const MAIL_PATH = '/api/send-email';
export const SIGNED_URL = (filename: string) => `/api/signed-bucket-url?file=${filename}`;

export const CALENDLY_URL = 'https://calendly.com/d/dv3-tfw-97c/discovery-call-with-startup-house';
export const CALENDLY_ENABLED = process.env.NEXT_PUBLIC_CALENDLY_ENABLED === 'true';

export const CONTENT_MAX_WIDTH = 1920;
export const CONTENT_MAX_WIDTH_PX = CONTENT_MAX_WIDTH + 'px';
export const CONTENT_MARGIN_X = 5;

// Distance from the viewport edge to the content line: SectionContentWrapper's
// responsive paddingX plus, once the viewport exceeds CONTENT_MAX_WIDTH, the
// centering gap. Full-bleed slider rails pull themselves out by this much on
// both sides (negative margin) and pad back in, so cards snap to the content
// line while scrolled cards stay visible through the gutters all the way to
// the screen edges.
export const CONTENT_GUTTER = {
  base: `max(20px, calc((100vw - ${CONTENT_MAX_WIDTH_PX}) / 2 + 20px))`,
  sm: `max(24px, calc((100vw - ${CONTENT_MAX_WIDTH_PX}) / 2 + 24px))`,
  lg: `max(80px, calc((100vw - ${CONTENT_MAX_WIDTH_PX}) / 2 + 80px))`,
  '3xl': `max(96px, calc((100vw - ${CONTENT_MAX_WIDTH_PX}) / 2 + 96px))`,
} as const;

export const CONTENT_GUTTER_NEGATIVE = {
  base: `calc(-1 * ${CONTENT_GUTTER.base})`,
  sm: `calc(-1 * ${CONTENT_GUTTER.sm})`,
  lg: `calc(-1 * ${CONTENT_GUTTER.lg})`,
  '3xl': `calc(-1 * ${CONTENT_GUTTER['3xl']})`,
} as const;

export const TILE_WIDTH = 217;
export const TILE_WIDTH_PX = TILE_WIDTH + 'px';

export const EXTERNAL_URL = {
  EMAIL: 'hello@startup-house.com',
  INSTAGRAM: 'https://www.instagram.com/startuphousecom/',
  FACEBOOK: 'https://www.facebook.com/startuphousecom/',
  LINKEDIN: 'https://www.linkedin.com/company/startup-house-com',
  DRIBBBLE: 'https://dribbble.com/startup-house',
  GITHUB: 'https://github.com/startupdevhouse',
  TWITTER: 'https://twitter.com/startuphousecom',
};

export const HEADER_HEIGHT = '80px';
export const HIGHLIGHT_JS_URL = 'https://cdn.jsdelivr.net/gh/highlightjs/cdn-release@11.7.0/build/highlight.min.js';
export const HIGHLIGHT_JS_STYLE_URL =
  'https://cdn.jsdelivr.net/gh/highlightjs/cdn-release@11.7.0/build/styles/brown-paper.min.css';

export const CLUTCH_WIDGET_URL = 'https://widget.clutch.co';
export const CLUTCH_SCRIPT_URL = 'https://widget.clutch.co/static/js/widget.js';
export const CLUTCH_COMPANY_ID = '68037';

export const COOKIEYES_URL = 'https://cdn-cookieyes.com/client_data/6606ec9a9c939f70e9b34059/script.js';
export const COOKIEYES_TABLE_TAG = 'cky-audit-table-element';
export const COOKIEYES_BANNER_TAG = 'cky-banner-element';
export const COOKIEYES_SHOW_BTN_CLASS = 'show-cookieyes-btn';
export const PATHS_TO_SHOW_COOKIEYES_BUTTON = [GeneralPath.PRIVACY_POLICY];

export const COMPANY_FOUNDATION_YEAR = 2016;

export const UTM_SOURCE_LS_KEY = 'UTM_SOURCE';

// in order not to slow down page initial load we delay preloaded images download
export const PRELOADED_IMAGES_DELAY = 2000;
export const PRELOADED_IMAGES: string[] = [];
export const DECREASE_SCROLL_TOP_VALUE = 40;
export const ALPHABET_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXZ'.split('');
export const ALPHABET: Alphabet[] = ALPHABET_LETTERS.map((letter) => {
  return {
    letter,
    titles: [],
  };
});

export const REGEX = {
  UNDERLINE_TEXT: /##(.*?)##/g,
  HIGHLIGHT_TEXT: /\*\*(.*?)\*\*/g,
  CIRCLED_TEXT: /\[(.*?)\]/g,
  EXCLAIMED_TEXT: /!!(.*?)!!/g,
};

export const PAGE_REVALIDATE_TIME = 60; // 60 seconds

export const LOCALE = {
  EN: 'en',
  PL: 'pl',
  DE: 'de',
  ES: 'es',
  FR: 'fr',
  NO: 'no',
  NL: 'nl',
  SV: 'sv',
  DA: 'da',
} as const;

export const DEFAULT_LOCALE: string = LOCALE.EN;

export enum LOCALES {
  ABOUT_US = 'about-us',
  ARTICLES = 'articles',
  AUTHORS = 'authors',
  BOT = 'bot',
  CASE_STUDY = 'case-study',
  CONTACT = 'contact',
  COMMON = 'common',
  CLOUD_TECH = 'cloud-tech',
  ED_TECH = 'ed-tech',
  AI_TECH = 'ai-tech',
  FINTECH = 'fintech',
  HEALTH_TECH = 'health-tech',
  TRAVEL_TECH = 'travel-tech',
  CYBERSECURITY = 'cybersecurity',
  FOOD_MANUFACTURING = 'food-manufacturing',
  EU_PROJECTS = 'eu-projects',
  HOME = 'home',
  INPRODUCT = 'inproduct',
  INVENTORY = 'inventory',
  SMARTSEARCH = 'smartsearch',
  LIBRARY = 'library',
  MENU = 'menu',
  MAPBOX = 'mapbox',
  MOBILE_DEVELOPMENT = 'mobile-development',
  MVP_DEVELOPMENT = 'mvp-development',
  NO_CODE = 'no-code',
  NOT_FOUND = 'not-found',
  PRIVACY_POLICY = 'privacy-policy',
  DIRECTION_CHECK = 'direction-check',
  PRODUCT_DISCOVERY = 'product-discovery',
  SCOPE_BUILDING = 'scope-building',
  USER_TESTING = 'user-testing',
  WEB_DEVELOPMENT = 'web-development',
  UX_DESIGN = 'ux-design',
  UI_DESIGN = 'ui-design',
  PRODUCT_DESIGN = 'product-design',
  QUALITY_ASSURANCE = 'quality-assurance',
  SDH_LABS = 'sdh-labs',
  DEDICATED_TEAM = 'dedicated-team',
  TEAM_AUGMENTATION = 'team-augmentation',
  THANKS = 'thanks',
  AI = 'ai',
  AI_CONTENT_POLICY = 'ai-content-policy',
  TERMS_OF_SERVICE = 'terms-of-service',
  AI_SERVICES = 'ai-services',
  DATA_SCIENCE = 'data-science',
  DATA_ANALYTICS = 'data-analytics',
  AI_INTERFACE_LAYER = 'ai-interface-layer',
  KNOWHUB = 'knowhub',
  IDEATION_SERVICES = 'ideation-services',
  DESIGN_SERVICES = 'design-services',
  SOFTWARE_DEVELOPMENT_SERVICES = 'software-development-services',
  AI_DATA_SCIENCE = 'ai-data-science',
  MULTI_AGENT_SYSTEMS = 'multi-agent-systems',
  AI_AGENTS_FOR_TRAVEL = 'ai-agents-for-travel',
  AI_AGENTS_FOR_RETAIL = 'ai-agents-for-retail',
  AI_AGENTS_FOR_MANUFACTURING = 'ai-agents-for-manufacturing',
  AI_AGENTS_FOR_HEALTHCARE = 'ai-agents-for-healthcare',
  AI_AGENTS_FOR_FINANCIAL_SERVICES = 'ai-agents-for-financial-services',
  AI_AGENTS_FOR_INDUSTRIAL_OPERATIONS = 'ai-agents-for-industrial-operations',
  COOPERATION_MODELS = 'cooperation-models',
  AI_NATIVE_POD = 'ai-native-pod',
  PLATFORM_ENGINEERING = 'platform-engineering',
  DIRECTORY = 'directory',
  DIRECTORY_ALL = 'directory-all',
  COMPLIANCE_COPILOT = 'compliance-copilot',
  AI_READINESS_QUIZ = 'ai-readiness-quiz',
}

export type AvailableLanguages = (typeof LOCALE)[keyof typeof LOCALE];

export const OG_IMAGE = {
  WIDTH: 1200,
  HEIGHT: 630,
};

export const SITE_DEFAULT_OG_IMAGE = '/sh-og-image.jpg';

// Localized variants of the site-default OG image (localized copy baked into the
// graphic), keyed by locale code. Pages configured with SITE_DEFAULT_OG_IMAGE get
// the variant matching the requested locale — see Seo.tsx.
export const SITE_DEFAULT_OG_IMAGE_BY_LOCALE: Partial<Record<string, string>> = {
  [LOCALE.EN]: '/og/og-image-en.jpg',
  [LOCALE.PL]: '/og/og-image-pl.jpg',
  [LOCALE.DE]: '/og/og-image-de.jpg',
  [LOCALE.ES]: '/og/og-image-es.jpg',
  [LOCALE.FR]: '/og/og-image-fr.jpg',
  [LOCALE.NO]: '/og/og-image-no.jpg',
  [LOCALE.NL]: '/og/og-image-nl.jpg',
  [LOCALE.SV]: '/og/og-image-sv.jpg',
  [LOCALE.DA]: '/og/og-image-da.jpg',
};
